import type { Actor } from '../../sim/actor';
import type { ClassDef, IdentityController } from '../../sim/registry';
import { T, tripod } from '../../sim/skills';
import type { ResolvedSkill, SkillDef } from '../../sim/skillTypes';
import { registerStatus } from '../../sim/status';
import { applyStatus, removeStatus } from '../../sim/statusSystem';
import type { World } from '../../sim/world';
import { classBaseStats } from '../balance';
import { area, basicAttack, circle, cone, dashSkill, hit, hits, identityOp, invuln, move, rect, self, skill, stage } from './kit';

/**
 * RAVAGER — greatblade juggernaut (burst-mode bruiser archetype).
 * Build Wrath with every hit, then Unleash into Frenzy: faster, crits more,
 * shrugs off flinches, and gains access to the Crimson Finale.
 */

const HUE = 0xff4a36;

registerStatus({ id: 'rav_frenzy', name: 'Frenzy', kind: 'buff', icon: 'rav_frenzy', description: 'Attack and move speed +20%, crit rate +25%, damage +20%.', stacking: 'refresh', mods: { atkSpeedPct: 0.2, moveSpeedPct: 0.2, critRate: 0.25, dmgPct: 0.2 }, color: 0xff3020 });
registerStatus({ id: 'rav_spent', name: 'Spent', kind: 'debuff', icon: 'rav_spent', description: 'Wrath cannot be gained.', stacking: 'refresh', color: 0x777777 });
registerStatus({ id: 'rav_warcry', name: 'Blood Oath', kind: 'buff', icon: 'rav_warcry', description: 'Attack power increased.', stacking: 'strongest', valueMod: 'atkPct', color: 0xff6040 });

const FRENZY_TIME = 15;
const SPENT_TIME = 8;

// ------------------------------------------------------------------ skills

const cleaverArc = skill({
  id: 'rav_cleaver', name: 'Cleaver Arc', hue: HUE,
  description: 'Two heavy sweeps in a wide arc in front of you.',
  castType: 'normal', cooldown: 6, mana: 40, positional: 'back',
  stages: [stage('Attack1', 0.55, [hit(0.16, cone(4, 120), 1.1, { reaction: { kind: 'flinch' } }), hit(0.34, cone(4, 120), 1.1, { reaction: { kind: 'knockback', power: 1 } })])],
});

const earthshaker = skill({
  id: 'rav_stomp', name: 'Earthshaker Stomp', hue: HUE,
  description: 'Stomp the ground, shaking foes around you. Counters blue-glowing enemies.',
  castType: 'normal', cooldown: 12, mana: 65, positional: 'head', superArmor: 'paralysis', counter: true, stagger: 'mid',
  stages: [stage('Slam', 0.65, [hit(0.3, circle(4), 4.8, { status: [{ id: 'quake', duration: 1.5 }], vfx: 'heavy' })])],
});

const bulwarkRush = skill({
  id: 'rav_rush', name: 'Bulwark Rush', hue: HUE,
  description: 'Charge 7 m forward, battering everything in your path.',
  castType: 'normal', cooldown: 14, mana: 70, positional: 'back', superArmor: 'push', stagger: 'low',
  stages: [stage('Dash', 0.5, [move(0, 7, 0.35), hits(0.05, 3, 0.1, rect(2.2, 2.2, 0), 1.8, { reaction: { kind: 'knockback', power: 2.5 } })])],
});

const gravefall = skill({
  id: 'rav_gravefall', name: 'Gravefall', hue: HUE,
  description: 'Hold to grind the blade into the earth, then release. Releasing in the Perfect Zone erupts for massive damage and launches foes.',
  castType: 'holding', cooldown: 36, mana: 125, positional: 'back', superArmor: 'paralysis', stagger: 'high', weakPoint: 1,
  stages: [],
  holding: {
    maxHold: 1.5, tickInterval: 0.25, anim: 'Charge', perfectZone: [1.3, 1.5], perfectMult: 2.45,
    tick: [hit(0, circle(3.5), 0.25)],
    finisher: stage('Slam', 0.7, [hit(0.15, circle(4.5), 4.5, { reaction: { kind: 'launch', power: 1, duration: 1 }, vfx: 'heavy' })]),
  },
});

const skywardRend = skill({
  id: 'rav_rend', name: 'Skyward Rend', hue: HUE,
  description: 'Rip upward to launch foes, then slam them back down.',
  castType: 'normal', cooldown: 16, mana: 80, positional: 'back', superArmor: 'paralysis', stagger: 'mid', weakPoint: 1,
  stages: [stage('Attack3', 0.8, [hit(0.2, rect(5, 2.5), 2.6, { reaction: { kind: 'launch', power: 0.5, duration: 0.9 } }), hit(0.55, rect(5, 2.5), 3.6, { reaction: { kind: 'knockdown', duration: 1.2 }, vfx: 'heavy' })])],
});

const tempestSpin = skill({
  id: 'rav_spin', name: 'Tempest Spin', hue: HUE,
  description: 'Hold to whirl your greatblade, moving at reduced speed and shredding everything around you.',
  castType: 'holding', cooldown: 24, mana: 100, positional: 'back', superArmor: 'paralysis', stagger: 'mid',
  stages: [],
  holding: { maxHold: 3, tickInterval: 0.3, anim: 'Spin', moveMult: 0.6, tick: [hit(0, circle(3), 0.75, { reaction: { kind: 'flinch' } })] },
});

const executionersDrop = skill({
  id: 'rav_drop', name: "Executioner's Drop", hue: HUE,
  description: 'Leap and bring the blade down in front of you with crushing force.',
  castType: 'normal', cooldown: 18, mana: 85, positional: 'back', superArmor: 'paralysis', stagger: 'high', weakPoint: 2,
  stages: [stage('Leap', 0.85, [move(0, 2, 0.3), hit(0.45, circle(2.5, 2), 8.2, { reaction: { kind: 'knockdown', duration: 1.2, power: 1 }, vfx: 'heavy' })])],
});

const bloodOathRoar = skill({
  id: 'rav_roar', name: 'Blood Oath Roar', hue: HUE,
  description: 'A war cry that wounds nearby foes, exposes them to your party (+6% damage taken) and raises your attack power.',
  castType: 'normal', cooldown: 24, mana: 100, superArmor: 'paralysis',
  synergy: { id: 'exposed', duration: 16 },
  stages: [stage('Buff', 0.6, [self(0.2, { id: 'rav_warcry', duration: 8, value: 0.08 }), hit(0.25, circle(5), 3, { reaction: { kind: 'knockback', power: 1.5 } })])],
});

const worldsunder = skill({
  id: 'rav_worldsunder', name: 'Worldsunder', hue: 0xff2a10,
  description: 'APEX ART. Charge a cataclysmic overhead strike. The longer you charge, the larger the blast; release in the overcharge window for maximum devastation.',
  castType: 'charge', cooldown: 300, mana: 0, kind: 'awakening', superArmor: 'full', stagger: 'max', weakPoint: 3,
  charge: { levels: [0.5, 1.0, 1.5], mults: [0.35, 0.5, 0.72, 1.0], overcharge: [1.5, 1.8], overMult: 1.17, areaScales: [0.71, 0.71, 0.86, 1], overAreaScale: 1.14, anim: 'Charge' },
  stages: [stage('Slam', 0.9, [invuln(0, 0.9), hit(0.2, circle(7), 36, { reaction: { kind: 'launch', power: 2, duration: 1.2 }, vfx: 'heavy' }), area(0.25, { shape: circle(8), delay: 0.25, hit: { shape: circle(8), coef: 0 }, telegraph: 'none', vfx: 'pillar' })])],
});

// Identity skills (Z/X).
const unleash = skill({
  id: 'rav_unleash', name: 'Unleash', hue: 0xff3020,
  description: 'Enter Frenzy for 15 s: +20% attack/move speed, +25% crit rate, +20% damage, paralysis immunity.',
  castType: 'normal', cooldown: 1, mana: 0, kind: 'identity', superArmor: 'full',
  stages: [stage('Buff', 0.5, [invuln(0, 0.5), hit(0.15, circle(4), 0.5, { reaction: { kind: 'knockback', power: 3 } }), identityOp(0.15, 'frenzy')])],
});

const crimsonFinale = skill({
  id: 'rav_finale', name: 'Crimson Finale', hue: 0xff1a10,
  description: 'Frenzy only. Two cross slashes and a rising impale. Consumes the remaining Frenzy for bonus damage.',
  castType: 'combo', cooldown: 1, mana: 0, kind: 'identity', superArmor: 'push', stagger: 'max', weakPoint: 2, positional: 'back', followWindow: 1.2,
  stages: [
    stage('Attack1', 0.45, [hit(0.15, rect(6, 3), 2.0, { reaction: { kind: 'flinch' } })]),
    stage('Attack2', 0.45, [hit(0.15, rect(6, 3), 2.0, { reaction: { kind: 'flinch' } })]),
    stage('Attack3', 0.8, [hit(0.3, rect(6, 3), 5.0, { reaction: { kind: 'launch', power: 1.5, duration: 1 }, vfx: 'heavy' }), identityOp(0.35, 'endFrenzy')]),
  ],
});

const grimLunge = skill({
  id: 'rav_lunge', name: 'Grim Lunge', hue: HUE,
  description: 'Outside Frenzy: a 4 m lunging thrust.',
  castType: 'normal', cooldown: 36, mana: 0, kind: 'identity', superArmor: 'push', stagger: 'high', weakPoint: 1,
  stages: [stage('Attack3', 0.6, [move(0, 4, 0.2), hit(0.2, rect(3, 2.5), 7, { reaction: { kind: 'knockback', power: 2 } })])],
});

// ------------------------------------------------------------------ tripods (facets)

const toHolding = (maxHold: number, ticks: number, coefTotal: number, anim = 'Spin') => (s: ResolvedSkill) => {
  s.castType = 'holding';
  s.holding = { maxHold, tickInterval: maxHold / ticks, anim, tick: [hit(0, cone(4.2, 180), coefTotal / ticks, { reaction: { kind: 'flinch' } })] };
  s.stages = [];
};

const tripods: ClassDef['tripods'] = {
  rav_cleaver: [
    tripod('rav_cleaver_t1a', 1, 'Quick Hands', 'Attack speed +30%.', T.atkSpeed(0.3)),
    tripod('rav_cleaver_t1b', 1, 'Keen Edge', 'Crit rate +40%.', T.crit(0.4)),
    tripod('rav_cleaver_t1c', 1, 'Frugal', 'Mana cost -67%.', T.mp(-0.67)),
    tripod('rav_cleaver_t2a', 2, 'Wide Arc', 'Sweep 180° and +1 m range.', T.custom((s) => {
      for (const st of s.stages) for (const a of st.actions) if (a.kind === 'hit' && a.hit.shape.kind === 'cone') a.hit.shape = { ...a.hit.shape, angle: Math.PI, radius: a.hit.shape.radius + 1 };
    })),
    tripod('rav_cleaver_t2b', 2, 'Serrate', 'Hits cause Bleed for 3 s (20% attack per second).', T.status({ id: 'bleed', duration: 3, value: 0.2 })),
    tripod('rav_cleaver_t2c', 2, 'Shoulder Check', 'Step 2 m forward before swinging.', T.addAction(move(0, 2, 0.12), 0)),
    tripod('rav_cleaver_t3a', 3, 'Echo Blade', 'An echo blade strikes again for 90%.', T.addAction(hit(0.5, cone(4, 140), 0.9), 0)),
    tripod('rav_cleaver_t3b', 3, 'Whirling Arc', 'Becomes a 1.5 s holding whirl hitting 6 times. Damage +250%, cooldown +4 s.', T.custom(toHolding(1.5, 6, 7.7)), T.cd(4)),
  ],
  rav_stomp: [
    tripod('rav_stomp_t1a', 1, 'Wide Quake', 'Area +30%.', T.aoe(0.3)),
    tripod('rav_stomp_t1b', 1, 'Quick Prep', 'Cooldown -3 s.', T.cd(-3)),
    tripod('rav_stomp_t1c', 1, 'Aftershock', 'Earthquake lasts 1 s longer.', T.custom((s) => {
      for (const st of s.stages) for (const a of st.actions) if (a.kind === 'hit') a.hit.status = [{ id: 'quake', duration: 2.5 }];
    })),
    tripod('rav_stomp_t2a', 2, 'Bedrock', 'Damage +40% to push-immune foes.', T.vsBoss(0.4)),
    tripod('rav_stomp_t2b', 2, 'Concussion', 'Stagger +1 tier.', T.stagger(1)),
    tripod('rav_stomp_t2c', 2, 'Stone Skin', 'Take 40% less damage while stomping.', T.damageReduction(0.4)),
    tripod('rav_stomp_t3a', 3, 'Seismic Echo', 'A second shockwave ring (4–7 m) follows for 90%.', T.addAction(hit(0.55, { kind: 'ring', inner: 3.5, outer: 7 }, 4.3), 0)),
    tripod('rav_stomp_t3b', 3, 'Faultline', 'The quake tears forward in an 8 m line instead. Damage +80%. No longer counters.', T.custom((s) => {
      for (const st of s.stages) for (const a of st.actions) if (a.kind === 'hit') a.hit.shape = rect(8, 2.5);
    }), T.dmg(0.8), T.counter(false)),
  ],
  rav_rush: [
    tripod('rav_rush_t1a', 1, 'Long Stride', 'Charge distance +4 m.', T.range(4), T.custom((s) => {
      for (const st of s.stages) for (const a of st.actions) if (a.kind === 'move') a.duration += 0.15;
    })),
    tripod('rav_rush_t1b', 1, 'Iron Hide', 'Gain a shield of 10% max HP for 4 s.', T.addAction({ at: 0, kind: 'hit', hit: { shape: circle(0.5), coef: 0, friendly: true, shieldPct: 0.1, shieldDuration: 4 } }, 0)),
    tripod('rav_rush_t1c', 1, 'Cinder Trail', 'Leave a burning trail for 3 s.', T.addAction(area(0.35, { shape: rect(7, 2.5, -7), anchor: 'self', delay: 0, duration: 3, tickInterval: 0.5, telegraph: 'none', hit: { shape: rect(7, 2.5, -7), coef: 0.3, element: 'fire' } }), 0)),
    tripod('rav_rush_t2a', 2, 'Double Charge', 'Can be used twice in a row (chain). Cooldown +5 s.', T.custom((s) => {
      s.castType = 'chain';
      s.stages = [s.stages[0]!, structuredClone(s.stages[0]!)];
      s.followWindow = 2.5;
      s.chainPenalty = 0;
    }), T.cd(5)),
    tripod('rav_rush_t2b', 2, 'Quick Prep', 'Cooldown -6 s.', T.cd(-6)),
    tripod('rav_rush_t2c', 2, 'Phase Rush', 'Pass through enemies and allies.', T.custom((s) => {
      for (const st of s.stages) for (const a of st.actions) if (a.kind === 'move') a.ignoreCollision = true;
    })),
    tripod('rav_rush_t3a', 3, 'Impact Burst', 'Explode at the end, launching foes for 145%.', T.addAction(hit(0.4, circle(3.5), 1.45 * 1.8, { reaction: { kind: 'launch', power: 0.5, duration: 0.9 }, vfx: 'heavy' }), 0)),
    tripod('rav_rush_t3b', 3, 'Quake Finish', 'End with a stomp that causes Earthquake for 3 s. Damage +60%.', T.addAction(hit(0.4, circle(3.5), 1.1, { element: 'earth', status: [{ id: 'quake', duration: 3 }] }), 0)),
  ],
  rav_gravefall: [
    tripod('rav_grave_t1a', 1, 'Wide Grave', 'Area +30%.', T.aoe(0.3)),
    tripod('rav_grave_t1b', 1, 'Leap In', 'Leap toward the cursor (up to 10 m) before grinding.', T.custom((s) => {
      s.maxRange = 10;
      s.preStage = stage('Leap', 0.3, [move(0, 10, 0.28, { toPoint: true, ignoreCollision: true })]);
    })),
    tripod('rav_grave_t1c', 1, 'Undertow', 'The first grind pulls nearby foes in.', T.custom((s) => {
      if (s.holding) s.holding.tick = [hit(0, circle(3.5), 0.25, { reaction: { kind: 'pull', power: 2 } })];
    })),
    tripod('rav_grave_t2a', 2, 'Frenzy Primed', 'Cooldown resets on Unleash; +50% damage during Frenzy.', T.custom((s) => s.tags = [...(s.tags ?? []), 'frenzyPrimed:0.5'])),
    tripod('rav_grave_t2b', 2, 'Magma Vent', 'A Perfect Zone release leaves lava for 6 s.', T.custom((s) => {
      s.holding?.finisher?.actions.push(area(0.2, { shape: circle(4), delay: 0, duration: 6, tickInterval: 1, telegraph: 'none', hit: { shape: circle(4), coef: 0.8, element: 'fire' } }));
    })),
    tripod('rav_grave_t2c', 2, 'Last Stand', 'Damage +50% while at or below 50% HP.', T.custom((s) => s.tags = [...(s.tags ?? []), 'lastStand:0.5'])),
    tripod('rav_grave_t3a', 3, 'Deep Fissure', 'Perfect Zone window +0.15 s; an extra eruption deals 120%.', T.custom((s) => {
      if (s.holding?.perfectZone) s.holding.perfectZone = [s.holding.perfectZone[0] - 0.15, s.holding.perfectZone[1]];
      s.holding?.finisher?.actions.push(hit(0.45, circle(5), 1.2 * 4.5));
    })),
    tripod('rav_grave_t3b', 3, 'Instant Grave', 'No holding: the finisher strikes immediately. Damage +90%, cooldown -12 s.', T.custom((s) => {
      const fin = s.holding?.finisher;
      s.castType = 'normal';
      s.holding = undefined;
      if (fin) s.stages = [fin];
      s.dmgMult *= 1.9 * 2.45;
    }), T.cd(-12)),
  ],
  rav_rend: [
    tripod('rav_rend_t1a', 1, 'Quick Prep', 'Cooldown -4 s.', T.cd(-4)),
    tripod('rav_rend_t1b', 1, 'Keen Strike', 'Crit rate +40%.', T.crit(0.4)),
    tripod('rav_rend_t1c', 1, 'Frugal', 'Mana cost -67%.', T.mp(-0.67)),
    tripod('rav_rend_t2a', 2, 'Hammerfall', 'Knock-down lasts 0.5 s longer.', T.custom((s) => {
      for (const st of s.stages) for (const a of st.actions) if (a.kind === 'hit' && a.hit.reaction?.kind === 'knockdown') a.hit.reaction = { ...a.hit.reaction, duration: (a.hit.reaction.duration ?? 1.2) + 0.5 };
    })),
    tripod('rav_rend_t2b', 2, 'Sunder', 'Ignore 40% of defence.', T.defPen(0.4)),
    tripod('rav_rend_t2c', 2, 'Wide Rend', 'Width +30%.', T.aoe(0.3)),
    tripod('rav_rend_t3a', 3, 'Fourfold Ascent', 'Strike 4 times in the air with fire. Damage +85%.', T.element('fire', { id: 'burn', duration: 3, value: 0.15 }), T.dmg(0.85)),
    tripod('rav_rend_t3b', 3, 'Cyclone Rise', 'Launch all foes around you (4 m). Damage +110%.', T.custom((s) => {
      for (const st of s.stages) for (const a of st.actions) if (a.kind === 'hit') a.hit.shape = circle(4);
    }), T.dmg(1.1)),
  ],
  rav_spin: [
    tripod('rav_spin_t1a', 1, 'Swift Feet', 'Move at full speed while spinning.', T.custom((s) => { if (s.holding) s.holding.moveMult = 1; })),
    tripod('rav_spin_t1b', 1, 'Endurance', 'Spin up to 2 s longer.', T.holdLonger(2)),
    tripod('rav_spin_t1c', 1, 'Quick Prep', 'Cooldown -12 s; spin 1 s shorter.', T.cd(-12), T.holdLonger(-1)),
    tripod('rav_spin_t2a', 2, 'Blade Wheel', 'Radius +40%; the outer ring deals +70%.', T.aoe(0.4), T.dmg(0.35)),
    tripod('rav_spin_t2b', 2, 'Iron Will', 'Gain push immunity while spinning.', T.superArmor('push')),
    tripod('rav_spin_t2c', 2, 'Parting Cleave', 'On release, dash 4 m with a slash for 45%.', T.custom((s) => {
      if (s.holding) s.holding.finisher = stage('Attack3', 0.4, [move(0, 4, 0.2), hit(0.15, rect(4, 2.5), 0.45 * 7.5)]);
    })),
    tripod('rav_spin_t3a', 3, 'Vortex', 'A full spin spawns a launching tornado (5 m) for 95%.', T.custom((s) => {
      if (s.holding) s.holding.finisher = stage('Attack3', 0.4, [area(0.1, { shape: circle(5), delay: 0.1, duration: 1.5, tickInterval: 0.5, telegraph: 'none', pull: 3, hit: { shape: circle(5), coef: 0.95 * 2.5, reaction: { kind: 'launch', power: 0.3, duration: 0.9 } } })]);
    })),
    // Twice the hits at 90% each = +80% total damage.
    tripod('rav_spin_t3b', 3, 'Twin Blades', 'Hit twice as often. Total damage +80%.', T.custom((s) => { if (s.holding) s.holding.tickInterval /= 2; }), T.dmg(-0.1)),
  ],
  rav_drop: [
    tripod('rav_drop_t1a', 1, 'Weak Point Focus', 'Weak point level +1.', T.weakPoint(1)),
    tripod('rav_drop_t1b', 1, 'Quick Hands', 'Attack speed +20%.', T.atkSpeed(0.2)),
    tripod('rav_drop_t1c', 1, 'Frugal', 'Mana cost -67%.', T.mp(-0.67)),
    tripod('rav_drop_t2a', 2, 'Keen Blow', 'Crit damage +50%.', T.critDmg(0.5)),
    tripod('rav_drop_t2b', 2, 'Sunder', 'Ignore 40% of defence.', T.defPen(0.4)),
    tripod('rav_drop_t2c', 2, 'Frenzy Primed', 'Cooldown resets on Unleash; +40% damage during Frenzy.', T.custom((s) => s.tags = [...(s.tags ?? []), 'frenzyPrimed:0.4'])),
    tripod('rav_drop_t3a', 3, 'Charged Drop', 'Becomes a 1 s charge skill; overcharge deals +200%. Cooldown +10 s.', T.custom((s) => {
      s.castType = 'charge';
      s.charge = { levels: [0.5, 1.0], mults: [0.5, 0.75, 1.0], overcharge: [1.0, 1.3], overMult: 3.0, anim: 'Charge' };
    }), T.cd(10)),
    tripod('rav_drop_t3b', 3, 'Ground Ripper', 'Also sends an 8 m shockwave forward. Damage +100%.', T.addAction(hit(0.5, rect(8, 2.2), 4.1), 0), T.dmg(0.5)),
  ],
  rav_roar: [
    tripod('rav_roar_t1a', 1, 'Quick Prep', 'Cooldown -6 s.', T.cd(-6)),
    tripod('rav_roar_t1b', 1, 'Booming', 'Area +40%.', T.aoe(0.4)),
    tripod('rav_roar_t1c', 1, 'Frugal', 'Mana cost -67%.', T.mp(-0.67)),
    tripod('rav_roar_t2a', 2, 'War Chant', 'Self attack buff becomes 14% for 12 s.', T.custom((s) => {
      for (const st of s.stages) for (const a of st.actions) if (a.kind === 'self') a.status = [{ id: 'rav_warcry', duration: 12, value: 0.14 }];
    })),
    tripod('rav_roar_t2b', 2, 'Intimidate', 'Monsters hit are feared for 2 s.', T.status({ id: 'fear', duration: 2 })),
    tripod('rav_roar_t2c', 2, 'Bloodletting', 'Exposed lasts 20 s.', T.synergy({ id: 'exposed', duration: 20 })),
    tripod('rav_roar_t3a', 3, 'Crimson Pulse', 'Damage +200%.', T.dmg(2)),
    tripod('rav_roar_t3b', 3, 'Fury Feed', 'Gain 15 Wrath on cast; deals no damage.', T.custom((s) => {
      for (const st of s.stages) {
        for (const a of st.actions) if (a.kind === 'hit') a.hit.coef = 0;
        st.actions.push(identityOp(0.2, 'gain', 15));
      }
    })),
  ],
};

// ------------------------------------------------------------------ identity

function frenzyBonus(s: SkillDef, a: Actor): number {
  let m = 1;
  for (const tag of s.tags ?? []) {
    const [k, v] = tag.split(':');
    if (k === 'frenzyPrimed' && a.player?.identity.mode === 'frenzy') m *= 1 + Number(v);
    if (k === 'lastStand' && a.hpPct <= 0.5) m *= 1 + Number(v);
  }
  return m;
}

const identity: IdentityController = {
  gaugeMax: 100,
  init(a) {
    const id = a.player!.identity;
    id.gauge = 0;
    id.mode = 'idle';
    id.timer = 0;
    id.data = { castGain: 0 };
  },
  onSkillCast(_w, a) {
    a.player!.identity.data.castGain = 0;
  },
  onDamageDealt(world, a, _skill, _amount, counter) {
    const id = a.player!.identity;
    if (id.mode !== 'idle') return;
    if (a.hasStatus('rav_spent')) return;
    const g = counter ? 10 : 1;
    const cap = 8 - (id.data.castGain ?? 0);
    const add = Math.max(0, Math.min(g, cap + (counter ? 10 : 0)));
    id.data.castGain = (id.data.castGain ?? 0) + add;
    id.gauge = Math.min(100, id.gauge + add * a.stats.identityGainMult);
    void world;
  },
  onKey(world, a, key) {
    const id = a.player!.identity;
    if (key === 'Z') {
      if (id.mode === 'idle' && id.gauge >= 100) {
        const s = a.skills.get('rav_unleash');
        return s ? world.useSkill(a, s, 'Z') : false;
      }
      return false;
    }
    const s = a.skills.get(id.mode === 'frenzy' ? 'rav_finale' : 'rav_lunge');
    return s ? world.useSkill(a, s, 'X') : false;
  },
  op(world, a, op, value) {
    const id = a.player!.identity;
    if (op === 'frenzy') {
      id.mode = 'frenzy';
      id.timer = FRENZY_TIME;
      a.superArmorBonus = 'paralysis';
      applyStatus(world, a, { id: 'rav_frenzy', duration: FRENZY_TIME }, a.id, a.stats.attack);
      // Frenzy Primed facets reset their cooldowns.
      for (const [sid, s] of a.skills) if (s.tags?.some((t) => t.startsWith('frenzyPrimed'))) a.cooldowns.delete(sid);
    } else if (op === 'endFrenzy') {
      endFrenzy(world, a);
    } else if (op === 'gain' && id.mode === 'idle') {
      id.gauge = Math.min(100, id.gauge + value);
    }
  },
  update(world, a, dt) {
    const id = a.player!.identity;
    if (id.mode === 'frenzy') {
      id.timer -= dt;
      id.gauge = Math.max(0, (100 * id.timer) / FRENZY_TIME);
      if (id.timer <= 0) endFrenzy(world, a);
    }
  },
  damageMult(a, s) {
    let m = frenzyBonus(s, a);
    if (s.id === 'rav_finale') m *= 1 + 0.02 * (a.player?.identity.gauge ?? 0);
    return m;
  },
  describe(a) {
    const id = a.player!.identity;
    return {
      label: id.mode === 'frenzy' ? 'FRENZY' : a.hasStatus('rav_spent') ? 'Spent' : 'Wrath',
      ready: id.mode === 'idle' && id.gauge >= 100,
      active: id.mode === 'frenzy',
    };
  },
};

function endFrenzy(world: World, a: Actor): void {
  const id = a.player!.identity;
  if (id.mode !== 'frenzy') return;
  id.mode = 'idle';
  id.gauge = 0;
  id.timer = 0;
  a.superArmorBonus = 'none';
  removeStatus(world, a, 'rav_frenzy');
  applyStatus(world, a, { id: 'rav_spent', duration: SPENT_TIME }, a.id, 0);
}

export const RAVAGER: ClassDef = {
  id: 'ravager',
  name: 'Ravager',
  title: 'Greatblade Juggernaut',
  archetype: 'Burst-mode bruiser',
  role: 'dps',
  description: 'A towering warrior with a serrated greatblade. Build Wrath with every blow, then Unleash a Frenzy of unstoppable, rapid swings that ends in a Crimson Finale.',
  hue: HUE,
  weapon: 'greatsword',
  model: 'hero_warrior',
  baseStats: (level) => classBaseStats(level, { hp: 1.15, atk: 1.0, speed: 0.98 }),
  skills: [cleaverArc, earthshaker, bulwarkRush, gravefall, skywardRend, tempestSpin, executionersDrop, bloodOathRoar],
  defaultBar: ['rav_cleaver', 'rav_stomp', 'rav_rush', 'rav_gravefall', 'rav_rend', 'rav_spin', 'rav_drop', 'rav_roar'],
  basic: basicAttack('rav_basic', 'Greatblade Strikes', HUE, { radius: 3, angle: 120, coefs: [0.5, 0.5, 0.9] }),
  dash: dashSkill('rav_dash', 'Heavy Roll', HUE, 7, 5),
  awakenings: [worldsunder],
  identitySkills: [unleash, crimsonFinale, grimLunge],
  tripods,
  identity,
  identityName: 'Wrath',
  identityDescription: 'Every hit builds Wrath (up to 8 per skill; counters grant 10). At 100 press Z to Unleash Frenzy for 15 s. In Frenzy, X performs the Crimson Finale; outside it, X is Grim Lunge. After Frenzy you are Spent for 8 s.',
};

